import { useEffect, useState, useRef } from "react";
import { Send, X } from "react-feather";
import api from "../api";
import { PaystackButton } from "react-paystack";

// 🔹 Inline popup for bulk SMS sending (Enhanced + Recharge Support)
const SMSBulkPopup = ({ show, onClose, tasks, onSuccess, type = "cash", smslink, runUuid }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [vendorSMS, setVendorSMS] = useState(0);
  const [vendorUsername, setVendorUsername] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState(200); // default top-up
  const errorRef = useRef(null);
  const [success, setSuccess] = useState("");
  const successRef = useRef(null);
  

  const generateRandomCode = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    return (
      letters.charAt(Math.floor(Math.random() * 26)) +
      letters.charAt(Math.floor(Math.random() * 26)) +
      Array.from({ length: 4 }, () =>
        numbers.charAt(Math.floor(Math.random() * 10))
      ).join("")
    );
  };

  const capitalizeWords = (str) =>
    str ? str.replace(/\b\w/g, (char) => char.toUpperCase()) : "";

  // ✅ Prefill vendor data & recipients
  useEffect(() => {
    if (show) {
      api
        .get(baseUrl + "/vendor-profile/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        })
        .then((res) => {
          setVendorSMS(res.data.sms_balance || 0);
          setVendorUsername(res.data.username || res.data.vendor_username || "");

            const prefill = tasks.map((t) => {
                const voucherCode = generateRandomCode();

                const phone =
                    t.cust_phone ||
                    t.phone ||
                    "";

                const name =
                    t.customer ||
                    t.customer_name ||
                    "Customer";
                
                const type =
                    t.type || "";

                const message =
                    type === "reward"
                    ? `${
                        t.suggestion || t.action?.message || ""
                        } Use code ${voucherCode}. STOP *456*95#`
                    : `${t.action?.message} STOP *456*95#` || "";

                return {
                    id: t.id,
                    name,
                    phone,
                    message,
                    total_amount: t.total_amount || t.potential_value || 0,
                    voucherCode,
                    suggestion: t.suggestion || t.action?.message,
                    type,
                };
            });

          setRecipients(prefill);
        })
        .catch(() => setError("Failed to fetch vendor data or credits."));
    }
  }, [show, tasks, type]);

  const handlePhoneChange = (i, value) => {
    setRecipients((prev) => {
      const updated = [...prev];
      updated[i].phone = value;
      return updated;
    });
  };

  const handleMessageChange = (i, value) => {
    setRecipients((prev) => {
      const updated = [...prev];
      updated[i].message = value;
      return updated;
    });
  };

  // ✅ Scroll to visible error
  const scrollToError = () => {
    setTimeout(() => {
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const scrollToSuccess = () => {
    setTimeout(() => {
      if (successRef.current) {
        successRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  // ✅ Send SMS Handler
  const handleSendAll = async () => {
    const validRecipients = recipients.filter((r) => r.phone && r.message);
    if (validRecipients.length === 0) {
      setError("Please fill in all phone numbers and messages.");
      return scrollToError();
    }

    if (vendorSMS < validRecipients.length) {
      setError(`Not enough SMS credits. You have ${vendorSMS} left.`);
      return scrollToError();
    }

    setSending(true);
    setError("");
    setSuccess("");

    try {
      const payload = validRecipients.map((r) => ({
          phone: r.phone,
          message: r.message,
          voucher_code: r.voucherCode,
          total_amount: r.total_amount,
          message_type: r.type,
          related_product: r.related_product || null,
          opportunity_id:r.id,
      }));
      
      const res = await api.post(
        baseUrl + "/vendor-send-retarget-sms/",
        { run_id: runUuid, bulk_messages: payload },
        { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } }
      );

      // ✅ NEW LOGIC: Accept queued async tasks as SUCCESS
      if (res.data?.success === true || res.data?.queued === true) {
        setSuccess("SMS queued successfully! Messages are being sent in the background.");
        scrollToSuccess();
        onSuccess();
        return;
      }

      // Unexpected response
      setError("Failed to send messages. Please try again.");
      scrollToError();

    } catch (err) {
      // If Celery is slow, Axios may timeout but queue is still success
      if (err?.response?.data?.queued) {
        setSuccess("SMS queued successfully!");
        scrollToSuccess();
        onSuccess();
        return;
      }

      setError("Error sending SMS. Please try again.");
      scrollToError();
    } finally {
      setSending(false);
    }
  };



  const vendorDataRaw = localStorage.getItem("VendorData");
  let vendorEmail = "demo@kadhr.com"; // fallback

  try {
    if (vendorDataRaw) {
      const vendorData = JSON.parse(vendorDataRaw);
      if (vendorData?.email) {
        vendorEmail = vendorData.email;
      }
    }
  } catch (err) {
    //console.error("Invalid VendorData JSON", err);
  }

  const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  // Generate a fallback transaction ref
  const makeTransRef = () => "TRANS-" + Math.floor(Math.random() * 99999999);

  const paystackProps = {
    email: vendorEmail,
    amount: rechargeAmount * 100,
    currency: "KES",
    metadata: { purpose: "SMS Recharge" },
    publicKey,
    text: "Confirm Recharge",

    onSuccess: async (reference) => {
      try {
        const trxref = reference?.reference || "N/A";

        await api.post(
          baseUrl + "/sms-checkout/",
          {
            total_amount: rechargeAmount,
            email: vendorEmail,
            trxref: trxref,
            trans: makeTransRef(),
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );
        setError('');
        setSuccess("Recharge successful!");
        scrollToSuccess();
        setShowRecharge(false);
        setVendorSMS((prev) => prev + rechargeAmount);


        setShowRecharge(false);
        setVendorSMS((prev) => prev + rechargeAmount); // directly add SMS credits
      } catch (error) {
        //console.error("Recharge API error:", error);
        //alert("Payment received, but failed to update SMS balance.");
      }
    },

    onClose: () => setShowRecharge(false),
  };

  if (!show) return null;

  return (
    <>
      {/* MAIN MODAL */}
      <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-lg mt70">
          <div className="modal-content">
            <div className="modal-header bg-lblue border-0 text-dark pr-3 pl-3 pb-2 pt-2">
              <h6 className="mb-0 col-11 font-bold">
                {type === "cash" ? "Send Message(s)" : "Send Message(s)"} (
                {recipients.length})
              </h6>
              <button onClick={onClose} className="col-1 btn text-right pr-0 btn-sm text-dark">
                <X size={18} />
              </button>
            </div>

            <div className="modal-body bg-lblue" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {recipients.map((r, i) => (
                <div key={i} className="shadow-tile border-dlblue border-0 rounded-4 p-3 mb-0 card mb-3">
                  <div className="fw-bold mb-0 font8">{r.name}</div>

                  <textarea
                    className="form-control font8 mb-0 hide"
                    rows="2"
                    placeholder={
                      type === "cash"
                        ? ""
                        : "Write your engagement message here (e.g. “Hey Jane, check out our new arrivals!”)"
                    }
                    value={r.message}
                    onChange={(e) => handleMessageChange(i, e.target.value)}
                    readOnly={type === "cash"}
                  />

                  {r.suggestion && (
                    <div className="text-muted font8 mt-1">
                      💡 <i>{r.suggestion}</i>
                    </div>
                  )}

                  <input
                    type="text"
                    className="form-control font9 mt-2 br-10"
                    placeholder="2547XXXXXXXX"
                    value={r.phone}
                    onChange={(e) => handlePhoneChange(i, e.target.value)}
                  />
                </div>
              ))}

              {error && (
                <div
                  ref={errorRef}
                  className="text-danger font9 mb-2 text-center fw-bold"
                >
                  ⚠️ {error}{" "}
                  {error.includes("Not enough SMS") && (
                    <span
                      className="badge bg-success pointer ms-2"
                      onClick={() => setShowRecharge(true)}
                    >
                      Recharge
                    </span>
                  )}
                </div>
              )}

              {success && (
                <div
                  ref={successRef}
                  className="text-success font9 mb-2 text-center fw-bold"
                >
                  ✅ {success}
                </div>
              )}
            </div>

            <div className="modal-footer border-0 bg-lblue p-3">
              <button
                className="btn btn-primary col-12 m-0"
                onClick={handleSendAll}
                disabled={sending}
              >
                {sending ? "Sending..." : <><Send size={14} /> Send All</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RECHARGE MODAL */}
      {showRecharge && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-sm mt70">
            <div className="modal-content">
              <div className="modal-header bg-default text-dark pb-2 pt-2">
                <h7 className="mb-0 col-11">Recharge SMS</h7>
                <button onClick={() => setShowRecharge(false)} className="btn btn-sm">
                  <X size={16} />
                </button>
              </div>

              <div className="modal-body">
                <p className="font9 text-muted mb-2">
                  Top up your SMS balance to continue sending messages.
                </p>

                <div className="mb-3">
                  <label className="form-label font9">Enter Amount (KES)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={rechargeAmount}
                    min="50"
                    onChange={(e) => setRechargeAmount(Number(e.target.value))}
                  />
                </div>

                <PaystackButton
                  {...paystackProps}
                  className="btn btn-dark col-12"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SMSBulkPopup;